import { useState } from 'react';
import { UserCircle, Activity, Plug, Users, Folder, MessageSquare, Network, GraduationCap, Zap, FolderOpen, Database, Globe, Monitor, Search, Plus, Play, Pause, MoreHorizontal, Check, Clock, ArrowUpRight, ChevronDown, X, Lock, Layers, KeyRound, Copy, Pencil, Grid3x3, Route as RouteIcon } from 'lucide-react';
import { TRACKS, GROUP_NAMES } from '../../data/workspaceTracks';
import { PulsesView } from './PulsesView';

// ── In-shell workspace views ─────────────────────────────────────────────────
// Every nav destination in the Giga chat shell renders here rather than routing
// out to the old dashboard. One shell, many views: the sidebar swaps the pane,
// nothing navigates.
// ─────────────────────────────────────────────────────────────────────────────

export type WorkspaceViewId = 'assistants' | 'pulses' | 'connections' | 'groups' | 'tracks' | 'chats' | 'workflows' | 'skills' | 'power-ups' | 'files' | 'tables' | 'browser' | 'viewer';
export const VIEW_META: Record<WorkspaceViewId, {
  title: string;
  blurb: string;
  icon: typeof UserCircle;
  action?: string;
}> = {
  assistants: {
    title: 'Assistants',
    blurb: 'Coworkers you brief once, then delegate to.',
    icon: UserCircle,
    action: 'New assistant'
  },
  pulses: {
    title: 'Pulses',
    blurb: 'Work that runs on a schedule without being asked.',
    icon: Activity,
    action: 'New pulse'
  },
  connections: {
    title: 'Connections',
    blurb: 'Accounts Giga signs into on your behalf.',
    icon: Plug,
    action: 'Connect a tool'
  },
  groups: {
    title: 'Groups',
    blurb: 'Who can see which knowledge. Access follows the group.',
    icon: Users,
    action: 'New group'
  },
  tracks: {
    title: 'Tracks',
    blurb: 'Projects and areas that keep their work together.',
    icon: RouteIcon,
    action: 'New track'
  },
  chats: {
    title: 'Chats',
    blurb: 'Every conversation, searchable.',
    icon: MessageSquare
  },
  workflows: {
    title: 'Workflows',
    blurb: 'Multi-step jobs you run on demand.',
    icon: Network,
    action: 'New workflow'
  },
  skills: {
    title: 'Skills',
    blurb: 'Recorded how-tos Giga reuses across jobs.',
    icon: GraduationCap,
    action: 'Record a skill'
  },
  'power-ups': {
    title: 'Power Ups',
    blurb: 'Extra abilities you can switch on per workspace.',
    icon: Zap
  },
  files: {
    title: 'Files',
    blurb: 'Documents Giga can read and cite.',
    icon: FolderOpen,
    action: 'Upload'
  },
  tables: {
    title: 'Tables',
    blurb: 'Structured data it can query and write rows to.',
    icon: Database,
    action: 'New table'
  },
  browser: {
    title: 'Browser',
    blurb: 'Sites Giga operates on your behalf.',
    icon: Globe
  },
  viewer: {
    title: 'Viewer',
    blurb: 'Watch a run happen live, step by step.',
    icon: Monitor
  }
};
const logo = (slug: string) => `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
const ASSISTANTS = [{
  name: 'Inbox triage',
  desc: 'Sorts overnight mail and surfaces only what needs a reply.',
  tools: ['gmail', 'googlecalendar'],
  runs: '412 runs',
  group: 'Leadership',
  private: true,
  active: true
}, {
  name: 'Pipeline analyst',
  desc: 'Watches deal movement and flags renewals going quiet.',
  tools: ['salesforce', 'slack'],
  runs: '96 runs',
  group: 'Revenue',
  private: false,
  active: true
}, {
  name: 'Meeting prep',
  desc: 'Builds a one-pager for every external meeting the night before.',
  tools: ['googlecalendar', 'notion'],
  runs: '188 runs',
  group: 'Marketing',
  private: false,
  active: true
}, {
  name: 'Support summariser',
  desc: 'Turns Intercom threads into weekly themes for product.',
  tools: ['intercom', 'linear'],
  runs: '54 runs',
  group: 'Operations',
  private: false,
  active: false
}];

// The catalogue. `accounts` matters more than a boolean: several tools support
// more than one signed-in account, and knowing there are two is the difference
// between "connected" and "connected as the wrong person".
type ConnCategory = 'Communication' | 'Productivity' | 'CRM & Sales' | 'Engineering' | 'Marketing' | 'Finance' | 'Data' | 'Design' | 'Support';
const CONNECTIONS: {
  name: string;
  slug: string;
  desc: string;
  category: ConnCategory;
  accounts: number;
  account?: string;
  popular?: boolean;
}[] = [{
  name: 'Gmail',
  slug: 'gmail',
  desc: 'Read, draft and send mail from your own address.',
  category: 'Communication',
  accounts: 2,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Google Drive',
  slug: 'google-drive',
  desc: 'Search and read documents across shared drives.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Google Calendar',
  slug: 'google-calendar',
  desc: 'See availability and schedule on your behalf.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Notion',
  slug: 'notion',
  desc: 'A collaborative workspace for docs and wikis.',
  category: 'Productivity',
  accounts: 1,
  account: 'Brightwave workspace',
  popular: true
}, {
  name: 'Slack',
  slug: 'slack',
  desc: 'Workplace messaging — post, read and reply in threads.',
  category: 'Communication',
  accounts: 1,
  account: 'brightwave.slack.com',
  popular: true
}, {
  name: 'Linear',
  slug: 'linear',
  desc: 'Product development and issue tracking.',
  category: 'Engineering',
  accounts: 1,
  account: 'brightwave',
  popular: true
}, {
  name: 'Google Sheets',
  slug: 'google-sheets',
  desc: 'Read and write rows in spreadsheets.',
  category: 'Data',
  accounts: 1,
  account: 'sarah.chen@brightwave.io'
}, {
  name: 'Intercom',
  slug: 'intercom',
  desc: 'Customer service platform for support conversations.',
  category: 'Support',
  accounts: 1,
  account: 'Brightwave'
}, {
  name: 'Salesforce',
  slug: 'salesforce',
  desc: 'The CRM of record for accounts and opportunities.',
  category: 'CRM & Sales',
  accounts: 0,
  popular: true
}, {
  name: 'HubSpot',
  slug: 'hubspot',
  desc: 'Customer platform and CRM for marketing and sales.',
  category: 'CRM & Sales',
  accounts: 0,
  popular: true
}, {
  name: 'Jira',
  slug: 'jira',
  desc: 'Issue and project tracking for software teams.',
  category: 'Engineering',
  accounts: 0,
  popular: true
}, {
  name: 'GitHub',
  slug: 'github',
  desc: 'Code hosting, pull requests and issues.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Figma',
  slug: 'figma',
  desc: 'Collaborative interface design and prototyping.',
  category: 'Design',
  accounts: 0
}, {
  name: 'Airtable',
  slug: 'airtable',
  desc: 'Structured bases Giga can query and update.',
  category: 'Data',
  accounts: 0
}, {
  name: 'Stripe',
  slug: 'stripe',
  desc: 'Financial infrastructure — payments and subscriptions.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Zoom',
  slug: 'zoom',
  desc: 'Meetings, recordings and transcripts.',
  category: 'Communication',
  accounts: 0
}, {
  name: 'Asana',
  slug: 'asana',
  desc: 'Work management for tasks and projects.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'Mailchimp',
  slug: 'mailchimp',
  desc: 'Email campaigns, audiences and reporting.',
  category: 'Marketing',
  accounts: 0
}, {
  name: 'Dropbox',
  slug: 'dropbox',
  desc: 'File storage Giga can read from and write to.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'QuickBooks',
  slug: 'quickbooks',
  desc: 'Accounting, invoices and expense records.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Canva',
  slug: 'canva',
  desc: 'Visual design and collaboration platform.',
  category: 'Design',
  accounts: 0
}, {
  name: 'Monday.com',
  slug: 'monday',
  desc: 'Work management platform for teams.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'ClickUp',
  slug: 'clickup',
  desc: 'Tasks, docs and goals in one workspace.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'Zapier',
  slug: 'zapier',
  desc: 'Trigger automations across thousands of apps.',
  category: 'Productivity',
  accounts: 1,
  account: 'brightwave-ops'
}, {
  name: 'Vercel',
  slug: 'vercel',
  desc: 'Deployments, previews and build logs.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Sentry',
  slug: 'sentry',
  desc: 'Error monitoring and release health.',
  category: 'Engineering',
  accounts: 1,
  account: 'brightwave'
}, {
  name: 'Supabase',
  slug: 'supabase',
  desc: 'Backend platform with a Postgres database.',
  category: 'Data',
  accounts: 0
}, {
  name: 'Cloudflare',
  slug: 'cloudflare',
  desc: 'DNS, caching and edge configuration.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Ahrefs',
  slug: 'ahrefs',
  desc: 'Search rankings, backlinks and keyword data.',
  category: 'Marketing',
  accounts: 1,
  account: 'brightwave.io'
}, {
  name: 'Apollo',
  slug: 'apollo',
  desc: 'Sales intelligence and prospect enrichment.',
  category: 'CRM & Sales',
  accounts: 0
}, {
  name: 'Clay',
  slug: 'clay',
  desc: 'Enrich and research accounts at scale.',
  category: 'CRM & Sales',
  accounts: 0
}, {
  name: 'Webflow',
  slug: 'webflow',
  desc: 'Website building, CMS and publishing.',
  category: 'Marketing',
  accounts: 0
}, {
  name: 'Ramp',
  slug: 'ramp',
  desc: 'Corporate cards, spend and receipts.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Zendesk',
  slug: 'zendesk',
  desc: 'Ticketing and helpdesk for customer support.',
  category: 'Support',
  accounts: 0
}, {
  name: 'Granola',
  slug: 'granola',
  desc: 'AI notepad that captures meeting notes.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io'
}, {
  name: 'Confluence',
  slug: 'confluence',
  desc: 'Team wiki and internal documentation.',
  category: 'Productivity',
  accounts: 0
}];
const CONN_CATEGORIES: ConnCategory[] = ['Communication', 'Productivity', 'CRM & Sales', 'Engineering', 'Marketing', 'Finance', 'Data', 'Design', 'Support'];
const CONN_PAGE_SIZE = 12;

// Which group owns each live credential. Only connected tools appear here —
// an integration nobody has signed into doesn't belong to anyone yet.
const CONN_GROUP: Record<string, string> = {
  Gmail: 'Company-wide',
  'Google Drive': 'Company-wide',
  'Google Calendar': 'Company-wide',
  Notion: 'Product & Engineering',
  Slack: 'Company-wide',
  Linear: 'Product & Engineering',
  'Google Sheets': 'Marketing',
  Intercom: 'Operations',
  Zapier: 'Operations',
  Sentry: 'Product & Engineering',
  Ahrefs: 'Marketing',
  Granola: 'Leadership'
};

// Custom credentials: the escape hatch for anything without a first-party
// integration. Each type asks for a different shape of secret.
const CREDENTIAL_TYPES = [{
  id: 'email',
  label: 'Email & Password',
  desc: 'For tools Giga signs into through the browser.',
  fields: [{
    label: 'Login URL',
    placeholder: 'https://app.example.com/login',
    type: 'url'
  }, {
    label: 'Email',
    placeholder: 'you@company.com',
    type: 'email'
  }, {
    label: 'Password',
    placeholder: '••••••••••••',
    type: 'password'
  }]
}, {
  id: 'api',
  label: 'API Key',
  desc: 'For tools with a token-based API.',
  fields: [{
    label: 'Base URL',
    placeholder: 'https://api.example.com',
    type: 'url'
  }, {
    label: 'API key',
    placeholder: 'sk_live_…',
    type: 'password'
  }, {
    label: 'Header name',
    placeholder: 'Authorization',
    type: 'text'
  }]
}, {
  id: 'sql-string',
  label: 'SQL (connection string)',
  desc: 'One URI for the whole database.',
  fields: [{
    label: 'Connection string',
    placeholder: 'postgres://user:pass@host:5432/db',
    type: 'password'
  }]
}, {
  id: 'sql-fields',
  label: 'SQL (fields)',
  desc: 'Host, port and credentials entered separately.',
  fields: [{
    label: 'Host',
    placeholder: 'db.example.com',
    type: 'text'
  }, {
    label: 'Port',
    placeholder: '5432',
    type: 'text'
  }, {
    label: 'Database',
    placeholder: 'production',
    type: 'text'
  }, {
    label: 'User',
    placeholder: 'readonly',
    type: 'text'
  }, {
    label: 'Password',
    placeholder: '••••••••••••',
    type: 'password'
  }]
}] as const;
const CHATS = [{
  name: 'Inbox catch-up',
  when: 'Today, 09:12',
  preview: 'Three things need you today — Acme redlines, Northwind seats, invoice 2214.'
}, {
  name: 'Tomorrow’s meetings',
  when: 'Today, 08:40',
  preview: 'Four meetings. Two need prep — Acme legal and Northwind kickoff.'
}, {
  name: 'Q3 pipeline review',
  when: 'Yesterday',
  preview: 'Revenue up 34% QoQ, CAC down 12%, one renewal unsigned.'
}, {
  name: 'Renewal risk in Salesforce',
  when: 'Yesterday',
  preview: 'Two accounts have gone quiet for 21+ days.'
}, {
  name: 'Draft reply to Acme legal',
  when: '2 days ago',
  preview: 'Position on the liability cap, plus data residency language.'
}, {
  name: 'Summarise the board deck',
  when: '3 days ago',
  preview: 'Slide 19 has an ask with no numbers behind it.'
}, {
  name: 'Who owns the migration?',
  when: 'Last week',
  preview: 'Priya, per the Linear project and the #eng thread.'
}, {
  name: 'Weekly marketing recap',
  when: 'Last week',
  preview: 'Paid down 8%, organic up 15%, two campaigns ended.'
}];
const WORKFLOWS = [{
  name: 'Tweet → Notion research table',
  steps: 4,
  runs: 128,
  success: '96%',
  last: '2 hours ago'
}, {
  name: 'New lead → enriched CRM record',
  steps: 6,
  runs: 341,
  success: '99%',
  last: '18 minutes ago'
}, {
  name: 'Support thread → Linear ticket',
  steps: 3,
  runs: 87,
  success: '92%',
  last: 'Yesterday'
}, {
  name: 'Invoice → finance tracker row',
  steps: 5,
  runs: 64,
  success: '100%',
  last: '4 days ago'
}];
const SKILLS = [{
  name: 'File a Linear bug from a support thread',
  used: 'Used 44 times',
  learned: 'Recorded by Priya'
}, {
  name: 'Build a weekly pipeline recap',
  used: 'Used 31 times',
  learned: 'Recorded by Sarah'
}, {
  name: 'Enrich a lead from a company domain',
  used: 'Used 128 times',
  learned: 'Recorded by Marcus'
}, {
  name: 'Draft a renewal follow-up',
  used: 'Used 19 times',
  learned: 'Recorded by Sarah'
}];
const POWER_UPS = [{
  name: 'Computer use',
  desc: 'Operates sites that have no API, in a sandboxed browser.',
  on: true
}, {
  name: 'Deep research',
  desc: 'Multi-source research runs that cite every claim.',
  on: true
}, {
  name: 'Voice notes',
  desc: 'Transcribes and acts on voice messages.',
  on: false
}, {
  name: 'Code interpreter',
  desc: 'Runs analysis on spreadsheets and CSV exports.',
  on: false
}];
const FILES = [{
  name: 'Q3 Board Review.pdf',
  meta: '24 slides',
  size: '4.2 MB',
  group: 'Leadership',
  visibility: 'Shared',
  when: 'Yesterday'
}, {
  name: 'Acme MSA redlines.docx',
  meta: '18 pages',
  size: '0.24 MB',
  group: 'Leadership',
  visibility: 'Private',
  when: '2 days ago'
}, {
  name: 'Pricing model v4.xlsx',
  meta: '9 sheets',
  size: '1.1 MB',
  group: 'Product & Engineering',
  visibility: 'Shared',
  when: 'Last week'
}, {
  name: 'Meeting notes — Northwind.md',
  meta: '2 pages',
  size: '0.03 MB',
  group: 'Leadership',
  visibility: 'Private',
  when: '3 days ago'
}, {
  name: 'Brand guidelines.pdf',
  meta: '32 pages',
  size: '8.4 MB',
  group: 'Marketing',
  visibility: 'Shared',
  when: 'Last month'
}];
const TABLES = [{
  name: 'Pipeline',
  rows: '1,284 rows',
  cols: '18 columns',
  source: 'Salesforce',
  group: 'Revenue'
}, {
  name: 'Content calendar',
  rows: '96 rows',
  cols: '11 columns',
  source: 'Notion',
  group: 'Marketing'
}, {
  name: 'Competitor research',
  rows: '312 rows',
  cols: '14 columns',
  source: 'Giga native',
  group: 'Marketing'
}, {
  name: 'Invoice tracker',
  rows: '208 rows',
  cols: '9 columns',
  source: 'Airtable',
  group: 'Operations'
}];

// Per-group counts. Deep, muted tile colours so seven monograms stay
// distinguishable without turning the list into a paint chart.
const GROUP_DETAIL: Record<string, {
  path: string;
  color: string;
  members: number;
  chats: number;
  tracks: number;
  credentials: number;
  files: number;
}> = {
  'Product & Engineering': {
    path: 'spaces/product-engineering',
    color: '#1F3A5F',
    members: 18,
    chats: 214,
    tracks: 9,
    credentials: 14,
    files: 342
  },
  Marketing: {
    path: 'spaces/marketing',
    color: '#22303C',
    members: 9,
    chats: 89,
    tracks: 13,
    credentials: 8,
    files: 208
  },
  'Company-wide': {
    path: 'spaces/company-wide',
    color: '#8E1F45',
    members: 42,
    chats: 79,
    tracks: 1,
    credentials: 5,
    files: 86
  },
  Revenue: {
    path: 'spaces/revenue',
    color: '#332C7A',
    members: 12,
    chats: 96,
    tracks: 2,
    credentials: 9,
    files: 164
  },
  'Customer Success': {
    path: 'spaces/customer-success',
    color: '#8C2340',
    members: 8,
    chats: 54,
    tracks: 2,
    credentials: 7,
    files: 112
  },
  Operations: {
    path: 'spaces/operations',
    color: '#4A3A24',
    members: 5,
    chats: 17,
    tracks: 2,
    credentials: 6,
    files: 88
  },
  Leadership: {
    path: 'spaces/leadership',
    color: '#3A2C4A',
    members: 4,
    chats: 35,
    tracks: 4,
    credentials: 5,
    files: 61
  },
  Demo: {
    path: 'spaces/entropy',
    color: '#7A1F32',
    members: 4,
    chats: 4,
    tracks: 1,
    credentials: 1,
    files: 12
  }
};
const BROWSER_SITES = [{
  name: 'portal.acme-legal.com',
  desc: 'Downloads countersigned contracts',
  runs: '12 runs'
}, {
  name: 'app.quickbooks.com',
  desc: 'Pulls invoice status for the finance tracker',
  runs: '64 runs'
}, {
  name: 'suppliers.northwind.io',
  desc: 'Checks stock levels weekly',
  runs: '8 runs'
}];

// ── Small shared pieces ──────────────────────────────────────────────────────

function Card({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="rounded-xl bg-app-surface p-5 shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-4145" data-mp="19gmcsib1rru4">{children}</div>;
}
function Row({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-center gap-4 border-b border-app-divider px-5 py-4 transition-colors duration-200 ease-out last:border-b-0 hover:bg-app-hover" data-id="element-4146" data-mp="37xdcsuo4coro">
      {children}
    </li>;
}
function List({
  children
}: {
  children: React.ReactNode;
}) {
  return <ul className="overflow-hidden rounded-xl bg-app-surface shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-4147" data-mp="3hor6glte2ucw">
      {children}
    </ul>;
}
function StatusDot({
  on
}: {
  on: boolean;
}) {
  return <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${on ? 'bg-emerald-500' : 'bg-neutral-300'}`} data-id="element-4148" data-mp="3rk2n59ocybna" />;
}
function ToolLogo({
  slug,
  size = 15
}: {
  slug: string;
  size?: number;
}) {
  return <img src={logo(slug)} alt="" aria-hidden="true" style={{
    width: size,
    height: size
  }} className="object-contain" data-id="element-4149" data-mp="4fmpqoor40cw" />;
}

// ── The view itself ──────────────────────────────────────────────────────────

export function WorkspaceViews({
  view,
  onOpenTrack
}: {
  view: WorkspaceViewId;
  onOpenTrack?: (trackName: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [connTab, setConnTab] = useState<'all' | 'popular' | 'connected'>('all');
  const [connCategory, setConnCategory] = useState<ConnCategory | 'all'>('all');
  const [connCatOpen, setConnCatOpen] = useState(false);
  const [connGroup, setConnGroup] = useState<string>('all');
  const [connPage, setConnPage] = useState(1);
  const [customOpen, setCustomOpen] = useState(false);
  const [customType, setCustomType] = useState<(typeof CREDENTIAL_TYPES)[number]['id'] | null>(null);
  const meta = VIEW_META[view];
  // Pulses carries its own list + detail surface, so it owns the whole pane.
  if (view === 'pulses') return <PulsesView data-id="element-4150" data-mp="23nlevvtdhd8n" />;
  const searchable = view === 'tracks' || view === 'chats' || view === 'files' || view === 'connections' || view === 'groups';
  const q = query.trim().toLowerCase();
  return <div className="mx-auto max-w-5xl px-6 pb-16 pt-16 md:px-8 md:pt-14" data-id="element-4151" data-mp="s74vjrep6cgi">
      <header className="flex flex-wrap items-end justify-between gap-4 pb-7" data-id="element-4152" data-mp="3oqnh5vecjkav">
        <div data-id="element-4153" data-mp="3jydkzxmn1o05">
          <h1 className="text-[22px] font-semibold tracking-[-0.015em] text-app" data-id="element-4154" data-mp="3jyne3u2zcba2">
            {meta.title}
          </h1>
          <p className="mt-1.5 max-w-[68ch] text-[14.5px] leading-6 text-app-muted" data-id="element-4155" data-mp="3f0c0r9dh7in3">{meta.blurb}</p>
        </div>
        {view === 'connections' ?
      /* Anything without a first-party integration comes in here, so
      the control names the secret it needs rather than just "Add". */
      <div className="relative" data-id="element-4156" data-mp="6l20m6t5cnhy">
            <button type="button" onClick={() => setCustomOpen(o => !o)} aria-expanded={customOpen} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-app bg-app-surface px-3.5 text-[13.5px] font-semibold text-app transition-colors duration-200 ease-out hover:border-app-strong hover:bg-app-hover" data-id="element-4157" data-mp="6hwbwsofjnmr">
              <Plus size={14} strokeWidth={2.5} data-id="element-4158" data-mp="2i9tb20fi1zrj" />
              Add custom
              <ChevronDown size={14} className={`text-app-subtle transition-transform duration-200 ${customOpen ? 'rotate-180' : ''}`} data-id="element-4159" data-mp="2ibmkfnhhyy38" />
            </button>
            {customOpen && <>
                <div className="fixed inset-0 z-30" onClick={() => setCustomOpen(false)} data-id="element-4160" data-mp="12qu3l2fikp0k" />
                <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-[280px] overflow-hidden rounded-xl border border-app bg-app-surface shadow-[0_20px_50px_-20px_rgba(20,20,24,0.28)]" data-id="element-4161" data-mp="16qw4389ijce9">
                  <p className="border-b border-app-divider px-4 py-2.5 text-[12px] font-semibold text-app-muted" data-id="element-4162" data-mp="1ghgx8g6f3ops">
                    Credential type
                  </p>
                  {CREDENTIAL_TYPES.map(t => <button key={t.id} type="button" onClick={() => {
              setCustomType(t.id);
              setCustomOpen(false);
            }} className="block w-full px-4 py-3 text-left transition-colors duration-200 ease-out hover:bg-app-hover" data-id="element-4163" data-mp="1qgobr6ow1peh">
                      <span className="block text-[14px] font-medium text-app" data-id="element-4164" data-mp="2yx5xhsh0pc">
                        {t.label}
                      </span>
                      <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4165" data-mp="13ewih3sz0doo">
                        {t.desc}
                      </span>
                    </button>)}
                </div>
              </>}
          </div> : meta.action && <button type="button" onClick={view === 'tracks' ? () => onOpenTrack?.('Inbound leads → qualified pipeline') : undefined} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-app-accent px-3.5 text-[13.5px] font-semibold text-white transition-colors duration-200 ease-out hover:bg-[var(--app-accent-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent)] focus-visible:ring-offset-2" data-id="element-4166" data-mp="18dr1a5qxu28s">
              <Plus size={14} strokeWidth={2.5} data-id="element-4167" data-mp="1r5jsbumg5ddv" />
              {meta.action}
            </button>}
      </header>

      {/* The form for the chosen credential type, inline above the list so the
       catalogue stays visible behind the decision. */}
      {view === 'connections' && customType && (() => {
      const t = CREDENTIAL_TYPES.find(c => c.id === customType)!;
      return <div className="mb-5 rounded-xl bg-app-surface p-5 shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-4168" data-mp="2oq1v4zv8i2i6">
              <div className="flex items-start justify-between gap-3" data-id="element-4169" data-mp="2aqse5xz7f4d5">
                <div data-id="element-4170" data-mp="2erm5l1buv1as">
                  <h2 className="text-[15px] font-semibold text-app" data-id="element-4171" data-mp="2equjqi828hxp">
                    Add custom tool · {t.label}
                  </h2>
                  <p className="mt-1 text-[13px] text-app-muted" data-id="element-4172" data-mp="20ywh8q8utkai">{t.desc}</p>
                </div>
                <button type="button" onClick={() => setCustomType(null)} aria-label="Cancel" className="text-app-subtle transition-colors duration-200 ease-out hover:text-app" data-id="element-4173" data-mp="25s8szzsfpwlu">
                  <X size={16} data-id="element-4174" data-mp="1w1wb6fc08hje" />
                </button>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-4175" data-mp="1i13bzharcy9y">
                <label className="block sm:col-span-2" data-id="element-4176" data-mp="1i3plhmh7lnut">
                  <span className="mb-1.5 block text-[13px] font-medium text-app" data-id="element-4177" data-mp="1mz4w85jx73n0">
                    Name
                  </span>
                  <input placeholder="What should Giga call this?" className="h-10 w-full rounded-lg border border-app px-3 text-[14px] text-app outline-none transition-colors duration-200 ease-out placeholder:text-app-subtle focus:border-app-accent" data-id="element-4178" data-mp="1f1uwlec1oczx" />
                </label>
                {t.fields.map(f => <label key={f.label} className={`block ${t.fields.length === 1 ? 'sm:col-span-2' : ''}`} data-id="element-4179" data-mp="11696t4xzlp6g">
                    <span className="mb-1.5 block text-[13px] font-medium text-app" data-id="element-4180" data-mp="w6wbrwwypbss">
                      {f.label}
                    </span>
                    <input type={f.type} placeholder={f.placeholder} className="h-10 w-full rounded-lg border border-app px-3 text-[14px] text-app outline-none transition-colors duration-200 ease-out placeholder:text-app-subtle focus:border-app-accent" data-id="element-4181" data-mp="mab7xewoi6qj" />
                  </label>)}
              </div>
              <div className="mt-4 flex items-center justify-between gap-4 border-t border-app-divider pt-3.5" data-id="element-4182" data-mp="2cn8vlql4llvq">
                <p className="flex items-center gap-1.5 text-[12.5px] text-app-muted" data-id="element-4183" data-mp="3ukcberwy29mw">
                  <Lock size={12} className="text-app-subtle" data-id="element-4184" data-mp="3dsssqkv9fe1" />
                  Stored in the vault. Never shown in a chat.
                </p>
                <button type="button" className="h-9 rounded-lg bg-app-accent px-4 text-[13.5px] font-semibold text-white transition-colors duration-200 ease-out hover:bg-[var(--app-accent-strong)]" data-id="element-4185" data-mp="87nhy9j8qmki">
                  Save credential
                </button>
              </div>
            </div>;
    })()}

      {searchable && <div className="relative mb-5" data-id="element-4186" data-mp="3kmxy316bi0yt">
          <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-app-subtle" data-id="element-4187" data-mp="3klwicjecwqar" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder={`Search ${meta.title.toLowerCase()}…`} className="h-10 w-full rounded-lg border border-app bg-app-surface pl-10 pr-4 text-[14px] text-app outline-none transition-colors duration-200 ease-out placeholder:text-app-subtle focus:border-app-accent" data-id="element-4188" data-mp="3pnlu619el7zw" />
        </div>}

      {/* Assistants — cards, because each one has a brief worth reading. */}
      {view === 'assistants' && <div className="grid gap-3 sm:grid-cols-2" data-id="element-4189" data-mp="3aww0au1knhys">
          {ASSISTANTS.map(a => <Card key={a.name} data-id="element-4190" data-mp="3aurwsd7l4kz1">
              <div className="flex items-start justify-between gap-3" data-id="element-4191" data-mp="35xje8biiyex9">
                <span className="flex items-center gap-2" data-id="element-4192" data-mp="2i8gk446petlv">
                  <StatusDot on={a.active} data-id="element-4193" data-mp="2i902tn46hm0y" />
                  <span className="text-[15px] font-semibold text-app" data-id="element-4194" data-mp="2d9dspmjnclwd">
                    {a.name}
                  </span>
                </span>
                <button type="button" aria-label={`More options for ${a.name}`} className="rounded-md p-1 text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-4195" data-mp="2rz1rag0g16ge">
                  <MoreHorizontal size={16} data-id="element-4196" data-mp="1zgo0v1hfsmrc" />
                </button>
              </div>
              <p className="mt-1.5 flex items-center gap-1.5 text-[12.5px] text-app-muted" data-id="element-4197" data-mp="1uhuqhhxg5bve">
                {a.private ? <Lock size={12} className="shrink-0 text-app-subtle" data-id="element-4198" data-mp="a01yw8no5bva" /> : <Users size={12} className="shrink-0 text-app-subtle" data-id="element-4199" data-mp="era8p5swuz8z" />}
                <span className="truncate" data-id="element-4200" data-mp="etn16cwaos0a">{a.group}</span>
                {a.private && <span className="shrink-0 rounded-md bg-app-subtle px-1.5 py-px text-[11px] font-medium text-app-muted" data-id="element-4201" data-mp="11gmkv8sit2y">
                    Private
                  </span>}
              </p>
              <p className="mt-3 text-[14px] leading-6 text-app-muted" data-id="element-4202" data-mp="ssmpfjfwb4vx">
                {a.desc}
              </p>
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-app-divider pt-3" data-id="element-4203" data-mp="xpwdaunmz8hn">
                <span className="flex items-center gap-1.5" data-id="element-4204" data-mp="jsr80wvqydng">
                  {a.tools.map(t => <ToolLogo key={t} slug={t} data-id="element-4205" data-mp="oo6j5h2ddvg6" />)}
                </span>
                <span className="text-[12.5px] text-app-subtle" data-id="element-4206" data-mp="34gmaijz8vfno">{a.runs}</span>
              </div>
            </Card>)}
        </div>}

      {view === 'connections' && <>
          {/* Three ways in: what most teams start with, what's already live,
           and the full catalogue. Counts sit inline so the tabs double as a
           read on how far along the workspace is. */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-app-divider" data-id="element-4207" data-mp="3r540y8au805r">
            <div className="flex items-center gap-5" data-id="element-4208" data-mp="3m6sp5s0zffkw">
              {([['all', 'All integrations', CONNECTIONS.length], ['popular', 'Popular', CONNECTIONS.filter(c => c.popular).length], ['connected', 'Connected', CONNECTIONS.filter(c => c.accounts > 0).length]] as const).map(([id, label, count]) => <button key={id} type="button" onClick={() => {
            setConnTab(id);
            setConnPage(1);
          }} className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 text-[14px] font-semibold transition-colors duration-200 ease-out ${connTab === id ? 'border-app-accent text-app' : 'border-transparent text-app-muted hover:text-app'}`} data-id="element-4209" data-mp="dykhsnzgszxp">
                  {label}
                  <span className="text-[12.5px] font-medium text-app-subtle" data-id="element-4210" data-mp="18zt5a1icfm7z">
                    {count}
                  </span>
                </button>)}
            </div>
            {/* Category narrows a long catalogue by the kind of work a tool does,
             which is how people look for one they haven't connected yet. */}
            <div className="relative -mb-px pb-1.5" data-id="element-4211" data-mp="1nxliuo4by0jj">
              <button type="button" onClick={() => setConnCatOpen(o => !o)} aria-expanded={connCatOpen} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-medium text-app-muted transition-colors duration-200 ease-out hover:text-app" data-id="element-4212" data-mp="1ryf5le61d9au">
                {connCategory === 'all' ? 'Category' : connCategory}
                <ChevronDown size={14} className={`text-app-subtle transition-transform duration-200 ${connCatOpen ? 'rotate-180' : ''}`} data-id="element-4213" data-mp="26m9z2wrvbhoz" />
              </button>
              {connCatOpen && <>
                  <div className="fixed inset-0 z-30" onClick={() => setConnCatOpen(false)} data-id="element-4214" data-mp="2fp1flazlp3nh" />
                  <div className="absolute right-0 top-[calc(100%+2px)] z-40 w-[190px] overflow-hidden rounded-xl border border-app bg-app-surface py-1 shadow-[0_20px_50px_-20px_rgba(20,20,24,0.28)]" data-id="element-4215" data-mp="3icvt7c6k40mw">
                    {(['all', ...CONN_CATEGORIES] as const).map(cat => <button key={cat} type="button" onClick={() => {
                setConnCategory(cat as ConnCategory | 'all');
                setConnCatOpen(false);
                setConnPage(1);
              }} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13.5px] font-medium text-app transition-colors duration-200 ease-out hover:bg-app-hover" data-id="element-4216" data-mp="38hc1l1z0zo3y">
                        <span className="truncate" data-id="element-4217" data-mp="3rbzdgjhgmm2z">
                          {cat === 'all' ? 'All categories' : cat}
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5" data-id="element-4218" data-mp="nw21q63nh62j">
                          <span className="text-[12px] text-app-subtle" data-id="element-4219" data-mp="sod55ixln9zu">
                            {cat === 'all' ? CONNECTIONS.length : CONNECTIONS.filter(c => c.category === cat).length}
                          </span>
                          {connCategory === cat && <Check size={13} strokeWidth={3} className="text-app-accent" data-id="element-4220" data-mp="28evums4n6e76" />}
                        </span>
                      </button>)}
                  </div>
                </>}
            </div>
          </div>

          {/* Connected only: filter by the group that owns the credential.
           Monogram tiles rather than a dropdown — with a handful of groups the
           colours are recognised faster than names are read. */}
          {connTab === 'connected' && <div className="mb-4 flex items-center gap-1.5 overflow-x-auto pb-1" data-id="element-4221" data-mp="23guas4ze6aun">
              <button type="button" onClick={() => {
          setConnGroup('all');
          setConnPage(1);
        }} aria-label="All groups" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ease-out ${connGroup === 'all' ? 'bg-app-subtle text-app' : 'text-app-subtle hover:bg-app-hover hover:text-app'}`} data-id="element-4222" data-mp="1ze7oejlhpi4u">
                <Grid3x3 size={15} data-id="element-4223" data-mp="2chtkujagkdwn" />
              </button>
              {GROUP_NAMES.filter(g => CONNECTIONS.some(c => c.accounts > 0 && CONN_GROUP[c.name] === g)).map(g => {
          const n = CONNECTIONS.filter(c => c.accounts > 0 && CONN_GROUP[c.name] === g).length;
          return <button key={g} type="button" onClick={() => {
            setConnGroup(g);
            setConnPage(1);
          }} title={`${g} · ${n} connected`} className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[11px] font-bold tracking-tight text-white transition-transform duration-200 ease-out ${connGroup === g ? 'ring-2 ring-[var(--app-text)] ring-offset-2' : 'opacity-75 hover:opacity-100'}`} style={{
            backgroundColor: GROUP_DETAIL[g].color
          }} data-id="element-4224" data-mp="2lgdrun6noz51">
                      {g.split(' ').slice(0, 2).map(w => w[0]).join('')}
                      <span className="absolute -right-1 -top-1 flex h-[15px] min-w-[15px] items-center justify-center rounded-full border-2 border-white bg-app-text px-1 text-[9.5px] font-bold text-white" data-id="element-4225" data-mp="1a0yn2x87h2zd">
                        {n}
                      </span>
                    </button>;
        })}
            </div>}

          {(() => {
        const matches = CONNECTIONS.filter(c => connTab === 'all' || (connTab === 'popular' ? c.popular : c.accounts > 0)).filter(c => connTab !== 'connected' || connGroup === 'all' || CONN_GROUP[c.name] === connGroup).filter(c => connCategory === 'all' || c.category === connCategory).filter(c => !q || c.name.toLowerCase().includes(q) || c.desc.toLowerCase().includes(q));
        const pages = Math.max(1, Math.ceil(matches.length / CONN_PAGE_SIZE));
        const page = Math.min(connPage, pages);
        const start = (page - 1) * CONN_PAGE_SIZE;
        const shown = matches.slice(start, start + CONN_PAGE_SIZE);
        return <>
                {/* Count first, then pagination — you need to know how big the
                 result is before deciding whether to page or refine. */}
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3" data-id="element-4226" data-mp="20vo62oo0k1ti">
                  <p className="text-[12.5px] text-app-muted" data-id="element-4227" data-mp="1m8lainj9438t">
                    {matches.length === 0 ? 'No integrations match' : <>
                        Showing {start + 1}–{Math.min(start + CONN_PAGE_SIZE, matches.length)} of{' '}
                        {matches.length} integration
                        {matches.length === 1 ? '' : 's'}
                      </>}
                  </p>
                  {pages > 1 && <div className="flex items-center gap-1" data-id="element-4228" data-mp="139z73209i01q">
                      <button type="button" onClick={() => setConnPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-app transition-colors duration-200 ease-out hover:bg-app-hover disabled:pointer-events-none disabled:text-app-subtle" data-id="element-4229" data-mp="187schstqqq0y">
                        Previous
                      </button>
                      <span className="px-1.5 text-[12.5px] text-app-muted" data-id="element-4230" data-mp="qwlqqpihptbl">
                        Page {page} of {pages}
                      </span>
                      <button type="button" onClick={() => setConnPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-app transition-colors duration-200 ease-out hover:bg-app-hover disabled:pointer-events-none disabled:text-app-subtle" data-id="element-4231" data-mp="10ore1lxsrmkg">
                        Next
                      </button>
                    </div>}
                </div>

                {matches.length === 0 ? <div className="rounded-xl bg-app-surface px-6 py-12 text-center shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-4232" data-mp="1jlatsu33w8pz">
                    <p className="text-[14.5px] font-semibold text-app" data-id="element-4233" data-mp="3leqihlfmvdib">
                      Nothing here
                    </p>
                    <p className="mx-auto mt-1.5 max-w-[38ch] text-[13.5px] leading-relaxed text-app-muted" data-id="element-4234" data-mp="3ggp3pcp8q0cn">
                      Try another category, or add it yourself with a custom
                      credential.
                    </p>
                  </div> :
          /* Two columns: a catalogue is scanned for a name, and a single
           tall column makes the eye travel much further to find one. */
          <div className="grid gap-2 sm:grid-cols-2" data-id="element-4235" data-mp="nyzcawx82ara">
                    {shown.map(c => <div key={c.name} className="group flex items-center gap-3 rounded-xl bg-app-surface px-4 py-3.5 shadow-[0_1px_2px_rgba(20,20,24,0.05)] transition-colors duration-200 ease-out hover:bg-app-hover" data-id="element-4236" data-mp="sy2iwlsuop1d">
                        <ToolLogo slug={c.slug} size={20} data-id="element-4237" data-mp="11qw0ipvrs0ie" />
                        <span className="min-w-0 flex-1" data-id="element-4238" data-mp="16oea8ihm2jrg">
                          <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4239" data-mp="16nwb9e3kdrfu">
                            {c.name}
                          </span>
                          {/* Accounts, not a boolean — two accounts is a
                           materially different state. The specific account is
                           held back until hover so the grid stays quiet. */}
                          {c.accounts > 0 ? <>
                              <span className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-app-muted group-hover:hidden" data-id="element-4240" data-mp="2r66raxtrj1db">
                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" data-id="element-4241" data-mp="2m8x1vi6dipph" />
                                {c.accounts} account
                                {c.accounts === 1 ? '' : 's'} connected
                              </span>
                              <span className="mt-0.5 hidden truncate text-[12.5px] text-app-muted group-hover:block" data-id="element-4242" data-mp="3a52jk3m295oi">
                                {c.accounts === 1 && c.account ? c.account : `${c.category} · managed in ${CONN_GROUP[c.name] ?? 'Everyone'}`}
                              </span>
                            </> : <span className="mt-0.5 block truncate text-[12.5px] text-app-muted" data-id="element-4243" data-mp="1p91vlfzto5v">
                              {c.desc}
                            </span>}
                        </span>
                        <button type="button" className="shrink-0 rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-app opacity-0 transition-opacity duration-200 ease-out hover:bg-app-hover group-hover:opacity-100" data-id="element-4244" data-mp="3ayu2o2onm84q">
                          {c.accounts > 0 ? 'Manage' : 'Connect'}
                        </button>
                      </div>)}
                  </div>}
              </>;
      })()}
        </>}

      {/* Groups — the counts ARE the content: they tell you at a glance which
       group actually holds the company's knowledge. Metrics sit as quiet
       pills on the right so scanning the names stays effortless. */}
      {view === 'groups' && <List data-id="element-4245" data-mp="3kykx64d7sn4t">
          {GROUP_NAMES.filter(g => !q || g.toLowerCase().includes(q)).map(g => {
        const d = GROUP_DETAIL[g];
        const shared = !g.toLowerCase().includes('private');
        return <Row key={g} data-id="element-4246" data-mp="29f8dk4z7yll2">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[12px] font-bold tracking-tight text-white" style={{
            backgroundColor: d.color
          }} data-id="element-4247" data-mp="21xjyq1d125tv">
                    {g.split(' ').slice(0, 2).map(w => w[0]).join('')}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-4248" data-mp="25zneh26qbwky">
                    <span className="flex items-center gap-2" data-id="element-4249" data-mp="2606v8fkn8exe">
                      <span className="truncate text-[14.5px] font-medium text-app" data-id="element-4250" data-mp="1i91iwadww4ru">
                        {g}
                      </span>
                      {!shared && <span className="shrink-0 rounded-md bg-app-subtle px-2 py-0.5 text-[11px] font-medium text-app-muted" data-id="element-4251" data-mp="1e6ejnoewvhkm">
                          Private
                        </span>}
                    </span>
                    <span className="mt-0.5 block truncate font-mono text-[12px] text-app-subtle" data-id="element-4252" data-mp="zj1vrt1mszr2">
                      {d.path}
                    </span>
                  </span>
                  <span className="hidden shrink-0 items-center gap-1 sm:flex" data-id="element-4253" data-mp="uie5xap52oyf">
                    {([[Users, d.members, 'members'], [MessageSquare, d.chats, 'chats'], [Layers, d.tracks, 'tracks'], [KeyRound, d.credentials, 'credentials'], [Copy, d.files, 'files']] as const).map(([Icon, n, label]) => <span key={label} title={`${n} ${label}`} className="inline-flex items-center gap-1 rounded-full bg-app-subtle px-2 py-1 text-[12px] font-medium text-app-muted" data-id="element-4254" data-mp="4t4zo41mcimj">
                        <Icon size={12} className="text-app-subtle" data-id="element-4255" data-mp="398o02deg1nb0" />
                        {n}
                      </span>)}
                  </span>
                  <button type="button" aria-label={`Edit ${g}`} className="shrink-0 rounded-md p-1 text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-4256" data-mp="3i07vapjvhf18">
                    <Pencil size={15} data-id="element-4257" data-mp="2pk5fk94duy2n" />
                  </button>
                </Row>;
      })}
        </List>}

      {view === 'tracks' && <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-id="element-4258" data-mp="2ljd7hj78yz7y">
          {TRACKS.filter(t => t.status === 'active').filter(t => !q || t.name.toLowerCase().includes(q)).map(t => <button key={t.name} type="button" onClick={() => onOpenTrack?.(t.name)} className="group rounded-xl bg-app-surface p-4 text-left shadow-[0_1px_2px_rgba(20,20,24,0.05)] transition-colors duration-200 ease-out hover:bg-app-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent)]" data-id="element-4259" data-mp="2g53fgngggylc">
                <span className="flex items-center justify-between gap-2" data-id="element-4260" data-mp="2u663v6uxakzf">
                  <RouteIcon size={16} className="text-app-subtle transition-colors duration-200 ease-out group-hover:text-app-muted" data-id="element-4261" data-mp="2z4r5naptpcpz" />
                  <StatusDot on data-id="element-4262" data-mp="2k65hfc1rbor0" />
                </span>
                <span className="mt-3 block truncate text-[14.5px] font-medium text-app" data-id="element-4263" data-mp="2p5rvtotbnb1e">
                  {t.name}
                </span>
                <span className="mt-1 block truncate text-[12.5px] text-app-muted" data-id="element-4264" data-mp="18r0dy5tsxm6c">
                  {t.group} · {t.type}
                </span>
              </button>)}
        </div>}

      {view === 'chats' && <List data-id="element-4265" data-mp="3gc8ljlej6p76">
          {CHATS.filter(c => !q || c.name.toLowerCase().includes(q) || c.preview.toLowerCase().includes(q)).map(c => <Row key={c.name} data-id="element-4266" data-mp="3bgjsm0x0o6ju">
                <span className="min-w-0 flex-1" data-id="element-4267" data-mp="36ngy9qkf0t2l">
                  <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4268" data-mp="36m5tbnf8hf9w">
                    {c.name}
                  </span>
                  <span className="mt-0.5 block truncate text-[12.5px] text-app-muted" data-id="element-4269" data-mp="2wpkjmos9rfzq">
                    {c.preview}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] text-app-subtle" data-id="element-4270" data-mp="2smnzgtn2zbto">
                  {c.when}
                </span>
              </Row>)}
        </List>}

      {view === 'workflows' && <List data-id="element-4271" data-mp="2ir31d6l4uvp7">
          {WORKFLOWS.map(w => <Row key={w.name} data-id="element-4272" data-mp="2is4edglpls9k">
              <span className="min-w-0 flex-1" data-id="element-4273" data-mp="ly19f14rbefl">
                <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4274" data-mp="lwq4gxzks0mw">
                  {w.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4275" data-mp="oi7cl6ljr0v3">
                  {w.steps} steps · {w.runs} runs · {w.success} success · last{' '}
                  {w.last}
                </span>
              </span>
              <button type="button" aria-label={`Run ${w.name}`} className="h-8 shrink-0 rounded-lg border border-app px-3 text-[12.5px] font-semibold text-app transition-colors duration-200 ease-out hover:border-app-strong hover:bg-app-hover" data-id="element-4276" data-mp="y8saertec1cy">
                Run
              </button>
            </Row>)}
        </List>}

      {view === 'skills' && <List data-id="element-4277" data-mp="ak92wei67slx">
          {SKILLS.map(s => <Row key={s.name} data-id="element-4278" data-mp="fgq0dzb9fjbo">
              <GraduationCap size={16} className="shrink-0 text-app-subtle" data-id="element-4279" data-mp="fgg988ehb64e" />
              <span className="min-w-0 flex-1" data-id="element-4280" data-mp="kgm4dyjyjar8">
                <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4281" data-mp="ke9cotoedm0z">
                  {s.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4282" data-mp="1vv97bvy7wcy6">
                  {s.learned}
                </span>
              </span>
              <span className="shrink-0 text-[12px] text-app-subtle" data-id="element-4283" data-mp="1thck61fcbsfm">
                {s.used}
              </span>
            </Row>)}
        </List>}

      {view === 'power-ups' && <div className="grid gap-3 sm:grid-cols-2" data-id="element-4284" data-mp="23g1u980zw8lb">
          {POWER_UPS.map(p => <Card key={p.name} data-id="element-4285" data-mp="1yg73d4h7zj09">
              <div className="flex items-start justify-between gap-3" data-id="element-4286" data-mp="1yf5sb007ewij">
                <span className="text-[15px] font-semibold text-app" data-id="element-4287" data-mp="2cf6rmb0tlvvw">
                  {p.name}
                </span>
                <span className={`mt-0.5 flex h-[18px] w-[32px] shrink-0 items-center rounded-full px-[3px] transition-colors duration-200 ease-out ${p.on ? 'justify-end bg-app-text' : 'justify-start bg-[var(--app-border-strong)]'}`} data-id="element-4288" data-mp="28d231qp8bpg4">
                  <span className="h-3 w-3 rounded-full bg-white" data-id="element-4289" data-mp="2h8i34pgkv0u5" />
                </span>
              </div>
              <p className="mt-2 text-[14px] leading-6 text-app-muted" data-id="element-4290" data-mp="2w1bimavungyt">
                {p.desc}
              </p>
            </Card>)}
        </div>}

      {view === 'files' && <List data-id="element-4291" data-mp="3arpl8xl9f09o">
          {FILES.filter(f => !q || f.name.toLowerCase().includes(q)).map(f => <Row key={f.name} data-id="element-4292" data-mp="3eqyjaenc1kb1">
                <FolderOpen size={16} className="shrink-0 text-app-subtle" data-id="element-4293" data-mp="3er6snwk8y7hh" />
                <span className="min-w-0 flex-1" data-id="element-4294" data-mp="30rfadjmp64xo">
                  <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4295" data-mp="35rbhhrqoychg">
                    {f.name}
                  </span>
                  <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4296" data-mp="2r1lyehin7nsw">
                    {f.meta} · {f.group}
                  </span>
                </span>
                {/* Size and who can see it — the two things you check before
                 sharing or deleting, so they sit on the row itself. */}
                <span className="hidden shrink-0 items-center gap-3 text-[12.5px] text-app-muted sm:flex" data-id="element-4297" data-mp="2n122fh9t2q8c">
                  <span data-id="element-4298" data-mp="284b99pqq6ar6">{f.size}</span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-4299" data-mp="2d2cscao5ia2n">
                    <Copy size={12} className="text-app-subtle" data-id="element-4300" data-mp="2d4gx0utvjd3z" />
                    {f.visibility}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] text-app-subtle" data-id="element-4301" data-mp="1p716ccrk4pyy">
                  {f.when}
                </span>
              </Row>)}
        </List>}

      {view === 'tables' && <List data-id="element-4302" data-mp="1f9d99g6hb6dm">
          {TABLES.map(t => <Row key={t.name} data-id="element-4303" data-mp="1fa69rzeugznb">
              <Database size={16} className="shrink-0 text-app-subtle" data-id="element-4304" data-mp="1f70h6a70bfmu" />
              <span className="min-w-0 flex-1" data-id="element-4305" data-mp="2cvke8r6p30qi">
                <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4306" data-mp="27xsitn6ucwhk">
                  {t.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4307" data-mp="22xo3i0ho67sx">
                  {t.rows} · {t.cols} · {t.group}
                </span>
              </span>
              <span className="shrink-0 rounded-full bg-app-subtle px-2.5 py-1 text-[11.5px] font-medium text-app-muted" data-id="element-4308" data-mp="rjajx7ty8eip">
                {t.source}
              </span>
            </Row>)}
        </List>}

      {view === 'browser' && <List data-id="element-4309" data-mp="2y8knf134r4y3">
          {BROWSER_SITES.map(s => <Row key={s.name} data-id="element-4310" data-mp="2y9m53oenipos">
              <Globe size={16} className="shrink-0 text-app-subtle" data-id="element-4311" data-mp="336mea86ss8k4" />
              <span className="min-w-0 flex-1" data-id="element-4312" data-mp="334t6uqod1kb2">
                <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4313" data-mp="385p80whbuscs">
                  {s.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4314" data-mp="3cya301byzbhw">
                  {s.desc}
                </span>
              </span>
              <span className="shrink-0 text-[12px] text-app-subtle" data-id="element-4315" data-mp="3lx3trb2md7nj">
                {s.runs}
              </span>
            </Row>)}
        </List>}

      {/* Viewer — one live run, because that is the whole point of the screen. */}
      {view === 'viewer' && <div className="overflow-hidden rounded-xl bg-app-surface shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-4316" data-mp="3vus0z0hew7ml">
          <div className="flex items-center justify-between gap-3 border-b border-app-divider px-5 py-4" data-id="element-4317" data-mp="1wkjumidti12g">
            <span className="flex items-center gap-2" data-id="element-4318" data-mp="21jmrvgnk32zv">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-app-accent" data-id="element-4319" data-mp="78my0vec433w" />
              <span className="text-[14.5px] font-semibold text-app" data-id="element-4320" data-mp="2bdbpom8s7ka">
                New lead → enriched CRM record
              </span>
            </span>
            <span className="text-[12.5px] text-app-muted" data-id="element-4321" data-mp="h1c6dhel90v0">
              Step 4 of 6 · 00:38
            </span>
          </div>
          <ol className="divide-y divide-[var(--app-divider)]" data-id="element-4322" data-mp="3kfyim75zjva5">
            {[{
          label: 'Read the inbound form submission',
          state: 'done'
        }, {
          label: 'Looked up the company domain',
          state: 'done'
        }, {
          label: 'Pulled headcount and funding from the web',
          state: 'done'
        }, {
          label: 'Writing the record to Salesforce',
          state: 'running'
        }, {
          label: 'Assigning an owner by territory',
          state: 'pending'
        }, {
          label: 'Posting a summary to #sales',
          state: 'pending'
        }].map(s => <li key={s.label} className="flex items-center gap-3 px-5 py-3.5" data-id="element-4323" data-mp="1c7jrxw46llc6">
                {s.state === 'done' ? <Check size={14} strokeWidth={3} className="shrink-0 text-emerald-500" data-id="element-4324" data-mp="1h8pqiakiw2tm" /> : s.state === 'running' ? <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-app-accent border-t-transparent" data-id="element-4325" data-mp="138yc48q3gkf3" /> : <span className="h-3.5 w-3.5 shrink-0 rounded-full border-2 border-app" data-id="element-4326" data-mp="17a0dow7hh8k8" />}
                <span className={`text-[14px] ${s.state === 'pending' ? 'text-app-subtle' : 'text-app'}`} data-id="element-4327" data-mp="1v0e1gaj1cl90">
                  {s.label}
                </span>
              </li>)}
          </ol>
          <div className="flex items-center justify-between gap-3 border-t border-app-divider px-5 py-3.5" data-id="element-4328" data-mp="1m5zls6ulf8pd">
            <span className="text-[12.5px] text-app-muted" data-id="element-4329" data-mp="1q6t9p081cni9">
              412 credits spent so far
            </span>
            <button type="button" className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-app transition-colors duration-200 ease-out hover:text-app-accent" data-id="element-4330" data-mp="2dwnhv4hetnw6">
              Open full trace
              <ArrowUpRight size={13} data-id="element-4331" data-mp="3k3ku3h3ckt9o" />
            </button>
          </div>
        </div>}
    </div>;
}